import clsx from 'clsx';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type Key,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronLeftIcon, ChevronRightIcon } from '@dynatrace/strato-icons';

import { RenderTab } from './RenderTab.js';
import { Tab, type TabProps } from './Tab.js';
import { TabPanel } from './TabPanel.js';
import {
  tabsArrowButtonCSS,
  tabsArrowWrapperCSS,
  tabsCSS,
  tabsWrapperCSS,
  tabsWrapperOverflowCSS,
} from './Tabs.sty.js';
import { Button } from '../../buttons/button/Button.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useResizeObserver } from '../../core/hooks/useResizeObserver.js';
import { useRovingFocus } from '../../core/hooks/useRovingFocus.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';

const COMPONENT_NAME = 'Tab';

// #region Types
/**
 * @public
 */
export interface TabsBaseProps
  extends StylingProps, DataTestId, MaskingProps, BehaviorTrackingProps {
  /**
   * Whether the whole tab group is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
  /** The element's unique identifier. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id). */
  id?: string;
  /** The collection of tabs to display. */
  children: ReactNode;
  /** The overflow behavior of the tab panel. */
  panelOverflow?: 'hidden' | 'scroll-y';
}

/**
 * @public
 */
export type TabsProps = TabsBaseProps &
  (TabsControlledProps | TabsUncontrolledProps);

/**
 * @public
 */
export interface TabsUncontrolledProps {
  /** The index of the selected tab. */
  selectedIndex?: never;
  /** The default index of the selected tab. */
  defaultIndex?: number;
  /** Handler that is called when the selected tab changes. */
  onChange?: never;
}

/**
 * @public
 */
export interface TabsControlledProps {
  /** The index of the selected tab. */
  selectedIndex: number;
  /** Handler that is called when the selected tab changes. */
  onChange: (selectedIndex: number) => void;
  /** The default index of the selected tab. */
  defaultIndex?: never;
}
// #endregion

/**
 * Use `Tabs` to organize related content into distinct views that users navigate one at a time. Selecting a tab replaces the entire content area.
 * @public
 */
export const Tabs = /* @__PURE__ */ forwardRef<HTMLDivElement, TabsProps>(
  (props, forwardedRef) => {
    const {
      disabled: disabledTabsProp = false,
      selectedIndex: selectedIndexProp,
      defaultIndex: defaultIndexProp,
      onChange: onChangeProp,
      children,
      panelOverflow = 'hidden',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const intl = useIntl();

    const generatedId = useId();
    const tabsUniqueId = props.id ?? generatedId;
    const tabsListId = useId();

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const childrenArray: ReactElement<TabProps>[] = useMemo(() => {
      return (
        Children.map(children, (child, i) => {
          if (isSlot(child, Tab)) {
            return cloneElement(child, {
              key: `${tabsUniqueId}-tab-${i}`,
            });
          } else if (isValidElement(child) && isDev()) {
            // eslint-disable-next-line no-console
            console.warn('Only Tab is a valid child of the Tabs component.');
          }
        }) || []
      );
    }, [children, tabsUniqueId]);

    // Get disabled keys from the children.
    // We handle the disabled keys on our own and not through the react-aria tablist.
    const tabsInfo = useMemo(() => {
      return childrenArray.reduce<{
        disabled: Set<Key>;
        keepMounted: Set<Key>;
        consumerIds: Record<string, string>;
      }>(
        (aggr, item) => {
          if (item.props.id && item.key) {
            aggr.consumerIds[item.key] = item.props.id;
          }
          if (item.props.disabled && item.key !== null) {
            aggr.disabled.add(item.key);
          }
          if (item.props.keepMounted && item.key !== null) {
            aggr.keepMounted.add(item.key);
          }
          return aggr;
        },
        {
          disabled: new Set(),
          keepMounted: new Set(),
          consumerIds: {},
        },
      );
    }, [childrenArray]);

    const [selectedIndex, setSelectedIndex] = useControllableState({
      defaultValue: defaultIndexProp ?? 0,
      value: selectedIndexProp,
      onChange: onChangeProp,
    });

    const ref = useRef<HTMLDivElement>(null);

    // When the selected tab is outside the visible area of the tablist (which
    // scrolls horizontally when tabs overflow), scroll it into view. Also account
    // for the arrow overlay buttons so the tab is not obscured behind them.
    //
    // Note: We intentionally avoid using `scrollIntoView()` here — it
    // can cause unwanted vertical page scrolling.
    useLayoutEffect(() => {
      const tabList = ref.current;
      if (!tabList) {
        return;
      }
      const selectedTab = tabList.querySelector<HTMLElement>(
        '[aria-selected="true"]',
      );
      if (!selectedTab) {
        return;
      }

      // Get current positions of the tablist container and selected tab
      const tabListRect = tabList.getBoundingClientRect();
      const tabRect = selectedTab.getBoundingClientRect();

      // Calculate offsets for arrow buttons that overlay the edges
      const leftArrow = tabList.previousElementSibling as HTMLElement | null;
      const rightArrow = tabList.nextElementSibling as HTMLElement | null;
      const leftOffset = leftArrow?.offsetWidth ?? 0;
      const rightOffset = rightArrow?.offsetWidth ?? 0;

      // Show part of adjacent tab to hint at more content
      const ADJACENT_TAB_PEEK_WIDTH_PX = 24;

      const isHiddenLeft = tabRect.left < tabListRect.left + leftOffset;
      const isHiddenRight = tabRect.right > tabListRect.right - rightOffset;

      if (isHiddenLeft) {
        // Scroll left to reveal the hidden tab at the desired position
        const targetLeftPosition =
          tabListRect.left + leftOffset + ADJACENT_TAB_PEEK_WIDTH_PX;
        const scrollAmount = targetLeftPosition - tabRect.left;
        tabList.scrollLeft -= scrollAmount;
      } else if (isHiddenRight) {
        // Scroll right to reveal the hidden tab at the desired position
        const targetRightPosition =
          tabListRect.right - rightOffset - ADJACENT_TAB_PEEK_WIDTH_PX;
        const scrollAmount = tabRect.right - targetRightPosition;
        tabList.scrollLeft += scrollAmount;
      }
    }, [selectedIndex]);

    const { containerRef } = useRovingFocus<HTMLDivElement>({
      direction: 'both',
      focusableChildrenSelector: '[type="button"],[role="tab"]',
      initialIndex: selectedIndex,
    });

    const rootRef = useMergeRefs([ref, containerRef]);

    // handle overflow and show/hide arrows
    const [isOverflowingLeft, setIsOverflowingLeft] = useState(false);
    const [isOverflowingRight, setIsOverflowingRight] = useState(false);

    const slideTabsLeft = (): void => {
      ref.current?.scrollBy({
        left: -ref.current.offsetWidth / 2,
        behavior: 'smooth',
      });
    };

    const slideTabsRight = (): void => {
      ref.current?.scrollBy({
        left: ref.current.offsetWidth / 2,
        behavior: 'smooth',
      });
    };

    const updateOverflow = useCallback(() => {
      if (ref.current) {
        const tabsWidth = ref.current.offsetWidth;
        const tabsScrollWidth = ref.current.scrollWidth;
        const scrollDiff = tabsScrollWidth - tabsWidth;
        const tabsScrollLeft = Math.ceil(ref.current.scrollLeft);
        setIsOverflowingRight(
          tabsScrollWidth > tabsWidth && tabsScrollLeft < scrollDiff,
        );
        setIsOverflowingLeft(
          tabsScrollWidth > tabsWidth && tabsScrollLeft !== 0,
        );
      }
    }, []);

    const { width: containerWidth, height: containerHeight } =
      useResizeObserver<HTMLDivElement>({
        ref: ref.current,
      });

    useLayoutEffect(() => {
      updateOverflow();
    }, [containerWidth, containerHeight, updateOverflow]);

    // Get the children relevant to the selected tab
    const getSelectedTabContent = (
      key: Key | null,
      childrenArray: ReactElement<TabProps>[],
    ): ReactNode | null => {
      const tab = childrenArray.find((child) => child.key === key);
      if (tab?.props?.children) {
        return tab.props.children;
      }
      return null;
    };

    const getTabPanelId = (
      key: Key | null,
      options?: { isSelected?: boolean; keepMounted?: boolean },
    ): string | undefined => {
      if (!options || options.keepMounted) {
        return key !== null ? `${key}-tabPanel` : undefined;
      }

      return key !== null && options.isSelected ? `${key}-tabPanel` : undefined;
    };
    // Memoize the tab panel for the selected tab to prevent unnecessary re-renders caused by the Tabs component re-rendering
    const MemoizedSelectedTabPanel = useMemo(
      () =>
        childrenArray.map((child, index) => {
          const isSelected = index === selectedIndex;
          const childKey = child.key;

          if (
            childKey !== null &&
            (tabsInfo.keepMounted.has(childKey) || isSelected)
          ) {
            return (
              <TabPanel
                key={childKey}
                style={{
                  display: !isSelected ? 'none' : '',
                }}
                id={getTabPanelId(childKey)}
                disabled={disabledTabsProp || tabsInfo.disabled.has(childKey)}
                panelOverflow={panelOverflow}
                aria-labelledby={tabsInfo.consumerIds[childKey] || childKey}
              >
                {getSelectedTabContent(childKey, childrenArray)}
              </TabPanel>
            );
          }
          return null;
        }),
      [childrenArray, disabledTabsProp, panelOverflow, selectedIndex, tabsInfo],
    );

    const handleKeyDown = useCallback(
      (event: ReactKeyboardEvent) => {
        const selectNextTab = () => {
          setSelectedIndex((curr) =>
            curr + 1 > childrenArray.length - 1 ? 0 : curr + 1,
          );
        };

        const selectPrevTab = () => {
          setSelectedIndex((curr) =>
            curr - 1 < 0 ? childrenArray.length - 1 : curr - 1,
          );
        };

        if (['ArrowRight', 'ArrowDown'].includes(event.code)) {
          selectNextTab();
        }

        if (['ArrowLeft', 'ArrowUp'].includes(event.code)) {
          selectPrevTab();
        }
      },
      [setSelectedIndex, childrenArray.length],
    );

    // Memoize the Tab list to prevent unnecessary re-renders to the Tab list caused by the Tabs component re-rendering
    const MemoizedTabsList = useMemo(() => {
      return childrenArray.map((item, index) => (
        <RenderTab
          key={item.key}
          tabItem={item}
          isSelected={
            selectedIndex === index && !item.props.disabled && !disabledTabsProp
          }
          isDisabled={item.props.disabled || disabledTabsProp}
          onActivate={() => {
            setSelectedIndex(index);
          }}
          onKeyDown={handleKeyDown}
          ariaControls={getTabPanelId(item.key, {
            keepMounted: item.props.keepMounted,
            isSelected:
              selectedIndex === index &&
              !item.props.disabled &&
              !disabledTabsProp,
          })}
        />
      ));
    }, [
      childrenArray,
      selectedIndex,
      disabledTabsProp,
      setSelectedIndex,
      handleKeyDown,
    ]);

    return (
      <div
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
        className={clsx(tabsCSS, consumerClassName)}
        style={consumerStyle}
        {...restProps}
        {...behavioralTrackingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <div className={tabsArrowWrapperCSS}>
          {isOverflowingLeft && (
            <Button
              className={tabsArrowButtonCSS}
              style={{ left: 0 }}
              variant="default"
              onClick={slideTabsLeft}
              aria-label={intl.formatMessage({
                defaultMessage: 'scroll tabs left',
                id: 'Cv2AP7VKybeuopCP',
                description: 'Aria label to scroll the tabs to the left',
              })}
              aria-controls={tabsListId}
            >
              <Button.Prefix>
                <ChevronLeftIcon />
              </Button.Prefix>
            </Button>
          )}
          <div
            className={clsx(tabsWrapperCSS, {
              [tabsWrapperOverflowCSS.left]:
                isOverflowingLeft && !isOverflowingRight,
              [tabsWrapperOverflowCSS.right]:
                !isOverflowingLeft && isOverflowingRight,
              [tabsWrapperOverflowCSS.both]:
                isOverflowingLeft && isOverflowingRight,
            })}
            role="tablist"
            aria-orientation="horizontal"
            id={tabsListId}
            ref={rootRef}
            tabIndex={-1}
            onScroll={updateOverflow}
          >
            {MemoizedTabsList}
          </div>
          {isOverflowingRight && (
            <Button
              className={tabsArrowButtonCSS}
              style={{ right: 0 }}
              onClick={slideTabsRight}
              aria-label={intl.formatMessage({
                defaultMessage: 'scroll tabs right',
                id: '2YBMATuaXx9/4zBP',
                description: 'Aria label to scroll the tabs to the right',
              })}
              aria-controls={tabsListId}
            >
              <Button.Suffix>
                <ChevronRightIcon />
              </Button.Suffix>
            </Button>
          )}
        </div>
        {MemoizedSelectedTabPanel}
      </div>
    );
  },
);
(Tabs as typeof Tabs & { displayName: string }).displayName = 'Tabs';
